import Map, { Layer, NavigationControl, Source } from 'react-map-gl/maplibre'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Drawer } from '../composants/common/Drawer'
import { useLocation, useNavigate, useNavigation } from 'react-router-dom'
import { AdresseSearch } from '../composants/adresse/AdresseSearch'
import Loader from '../composants/common/Loader'
import useNavigateWithPreservedSearchParams from '../hooks/useNavigateWithPreservedSearchParams'
import MapContext from '../contexts/map.context'
import { allBANLayers, interactiveLayers, staticCadastreLayers } from '../config/map/layers'
import { mapStyles } from '../config/map/styles'
import { StylesSwitch } from '../composants/map/StylesSwitch'
import { MaplibreStyleDefinition } from '../types/maplibre.types'
import { CadastreToggle } from '../composants/map/CadastreToggle'
import { AdresseSearchMap } from '../composants/map/AdresseSearchMap'
import { MapLibreEvent } from 'maplibre-gl'
import { SignalementsSearchMap } from '../composants/map/SignalementsSearchMap'
import { CreateAdresseButton } from '../composants/map/CreateAdresseButton'
import LayoutContext from '../contexts/layout.context'
import { CreateAlertButton } from '../composants/map/CreateAlertButton'
import { BaseLayout } from './BaseLayout'
import styled from 'styled-components'
import { PanoramaxToggle } from '../composants/map/PanoramaxToggle'
import { PanoramaxMap } from '../composants/map/PanoramaxMap'
import { PanoramaxLensDrag } from '../composants/map/PanoramaxLensDrag'
import { PANORAMAX_PICTURE_LAYER_ID } from '../config/map/panoramax'
import PanoramaxContext from '../contexts/panoramax.context'
import { SignalementContext } from '../contexts/signalement.context'
import { ReportLocationState } from '../hooks/useOpenReport'

interface MapLayoutProps {
  children?: React.ReactNode
}

const loadAssets = async (e: MapLibreEvent) => {
  const map = e.target
  if (!map) {
    return
  }

  const markerGreen = await map.loadImage('/icons/marker-green.png')
  map.addImage('marker-green', markerGreen.data)

  const markerOrange = await map.loadImage('/icons/marker-orange.png')
  map.addImage('marker-orange', markerOrange.data)

  const markerPurple = await map.loadImage('/icons/marker-purple.png')
  map.addImage('marker-purple', markerPurple.data)

  const coneGreen = await map.loadImage('/icons/cone-green.png')
  map.addImage('cone-green', coneGreen.data)

  const coneOrange = await map.loadImage('/icons/cone-orange.png')
  map.addImage('cone-orange', coneOrange.data)

  const conePurple = await map.loadImage('/icons/cone-purple.png')
  map.addImage('cone-purple', conePurple.data)

  const alertFlag = await map.loadImage('/icons/alert-flag.png')
  map.addImage('alert-flag', alertFlag.data)
}

const StyledMapWrapper = styled.div`
  flex: 1 1 auto;
  position: relative;
  height: 100%;
  overflow: hidden;
`

export function MapLayout({ children }: MapLayoutProps) {
  const [cursor, setCursor] = useState<string | null>(null)
  const onMouseEnter = useCallback(() => setCursor('pointer'), [])
  const onMouseLeave = useCallback(() => setCursor(null), [])
  const { showPanoramax, setShowPanoramax } = useContext(PanoramaxContext)
  const { signalement } = useContext(SignalementContext)

  useEffect(() => {
    setCursor(null)
  }, [signalement])

  const {
    mapRefCb,
    mapChildren,
    showCadastre,
    setShowCadastre,
    adresseSearchMapLayersOptions,
    signalementSearchMapLayerOptions,
    mapMessage,
    setMapMessage,
  } = useContext(MapContext)
  const { searchRef, drawerRef } = useContext(LayoutContext)

  const { navigate } = useNavigateWithPreservedSearchParams()
  const navigation = useNavigation()
  const location = useLocation()
  const routerNavigate = useNavigate()

  const handleCloseDrawer = () => {
    if ((location.state as ReportLocationState | null)?.backOnClose) {
      routerNavigate(-1)
      return
    }
    navigate('/')
  }

  return (
    <BaseLayout>
      <StyledMapWrapper className='map-wrapper'>
        <Map
          ref={mapRefCb}
          style={{
            zIndex: 0,
            position: 'absolute',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
          initialViewState={{
            longitude: 2,
            latitude: 47,
            zoom: 5,
          }}
          onLoad={loadAssets}
          mapStyle={mapStyles[0].uri}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          interactiveLayerIds={[
            ...interactiveLayers
              .filter(
                (layer) => !signalement || !allBANLayers.some((ban) => ban.layer.id === layer.id),
              )
              .map((layer) => layer.id),
            ...[PANORAMAX_PICTURE_LAYER_ID],
          ]}
          {...(cursor ? { cursor } : {})}
        >
          <Source
            id='cadastre'
            type='vector'
            url='https://openmaptiles.geo.data.gouv.fr/data/cadastre.json'
          >
            {staticCadastreLayers.map((cadastreLayer) => {
              return (
                <Layer
                  key={cadastreLayer.id}
                  {...(cadastreLayer as any)}
                  layout={{
                    ...cadastreLayer.layout,
                    visibility: showCadastre ? 'visible' : 'none',
                  }}
                />
              )
            })}
          </Source>
          <AdresseSearchMap options={adresseSearchMapLayersOptions} />
          <SignalementsSearchMap options={signalementSearchMapLayerOptions} />
          <PanoramaxMap />
          <PanoramaxLensDrag />
          {mapChildren}
          <NavigationControl position='top-right' />

          <CreateAdresseButton position='top-right' navigate={navigate} />
          <CreateAlertButton
            position='top-right'
            navigate={navigate}
            setMapMessage={setMapMessage}
          />
          <CadastreToggle
            layers={staticCadastreLayers.map((layer) => layer.id)}
            showCadastre={showCadastre}
            setShowCadastre={setShowCadastre}
            position='top-right'
          />
          <PanoramaxToggle
            showPanoramax={showPanoramax}
            setShowPanoramax={setShowPanoramax}
            position='top-right'
          />

          <StylesSwitch
            styles={mapStyles as [MaplibreStyleDefinition, MaplibreStyleDefinition]}
            position='bottom-right'
          />
        </Map>
        <AdresseSearch ref={searchRef} />
        {mapMessage && (
          <div
            style={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 3,
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              pointerEvents: 'none',
            }}
          >
            {mapMessage}
          </div>
        )}
        <Drawer ref={drawerRef} onClose={handleCloseDrawer}>
          {navigation.state === 'loading' ? (
            <div className='loader-wrapper'>
              <Loader />
            </div>
          ) : (
            children
          )}
        </Drawer>
      </StyledMapWrapper>
    </BaseLayout>
  )
}
